import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Tag, Upload, X, Copy, Check, Globe, MapPin,
  ExternalLink, User, ShieldCheck, Sparkles, AlertCircle, RefreshCw,
  Facebook, Linkedin, FileText, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../../store/auth.js';
import { apiGet } from '../../lib/api.js';
import InformationSection from './businessCard/InformationSection.jsx';
import AppearanceSection from './businessCard/AppearanceSection.jsx';
import BusinessCardPreview from './businessCard/BusinessCardPreview.jsx';
import CardLinkSection from './businessCard/CardLinkSection.jsx';
import { DEFAULT_APPEARANCE } from './businessCard/appearance.js';

/**
 * Settings > Digital Business Card: contact details, card look, link name and the
 * "attach to first-touch texts" switch.
 * @returns {JSX.Element}
 */
export default function DigitalBusinessCardSettings() {
  const navigate = useNavigate();
  const { agent, token, setSession } = useAuth();
  const fileInputRef = useRef(null);

  const [activeSubTab, setActiveSubTab] = useState('information'); // 'information' | 'appearance'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Form State
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    cellPhone: '',
    notes: '',
    licenseInfo: '',
    nationalId: '',
    bookingSlug: '',
    cardSlug: '',
    attachDigitalBusinessCard: true,
    cap72Hours: true,
    title: '',
    organization: '',
    workUrl: '',
    workEmail: '',
    addressLabel: '',
    street: '',
    city: '',
    state: 'CO',
    zip: '',
    facebook: '',
    linkedin: '',
    photoUrl: '',
    appearance: { ...DEFAULT_APPEARANCE },
  });

  const [initialForm, setInitialForm] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    apiGet('/user/vcard')
      .then((res) => {
        if (!alive) return;
        if (res?.ok) {
          const vc = res.vcard || {};
          const sc = res.scheduler || {};
          const addr = vc.address || {};
          const soc = vc.socials || {};

          // Auto-derive first/last from agent name if vcard name unpopulated
          const nameParts = String(res.agent?.name || agent?.name || '').trim().split(/\s+/);
          const autoFirst = nameParts[0] || '';
          const autoLast = nameParts.slice(1).join(' ') || '';

          const loaded = {
            firstName: vc.firstName ?? autoFirst,
            lastName: vc.lastName ?? autoLast,
            cellPhone: vc.cellPhone || agent?.phone || '',
            notes: vc.notes || '',
            licenseInfo: vc.licenseInfo || '',
            nationalId: vc.nationalId || '',
            bookingSlug: sc.bookingSlug || agent?.bookingSlug || '',
            cardSlug: res.cardSlug || '',
            attachDigitalBusinessCard: res.attachDigitalBusinessCard !== false,
            cap72Hours: sc.cap72Hours !== false,
            title: vc.title || agent?.title || 'Licensed Insurance Agent',
            organization: vc.organization || agent?.companyName || 'CIVIX Insurance Group',
            workUrl: vc.workUrl || agent?.businessWebsite || window.location.origin,
            workEmail: vc.workEmail || agent?.email || '',
            addressLabel: addr.label || '',
            street: addr.street || '',
            city: addr.city || '',
            state: addr.state || 'CO',
            zip: addr.zip || '',
            facebook: soc.facebook || '',
            linkedin: soc.linkedin || '',
            photoUrl: vc.photoUrl || agent?.avatar || '',
            appearance: { ...DEFAULT_APPEARANCE, ...(vc.appearance || {}) },
          };

          // No saved link name yet: pre-fill the suggestion so a Save assigns it.
          setForm({ ...loaded, cardSlug: loaded.cardSlug || res.suggestedCardSlug || '' });
          setInitialForm(loaded);
        }
      })
      .catch((err) => {
        toast.error('Could not load Digital Business Card details');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [agent]);

  const isDirty = initialForm && JSON.stringify(form) !== JSON.stringify(initialForm);

  function handleTextChange(field, val) {
    setForm((prev) => ({ ...prev, [field]: val }));
  }

  /**
   * @param {string|Object} field - Appearance key, or a partial patch object.
   * @param {*} [val] - Value when `field` is a key.
   */
  function handleAppearanceChange(field, val) {
    const patch = typeof field === 'object' ? field : { [field]: val };
    setForm((prev) => ({
      ...prev,
      appearance: { ...DEFAULT_APPEARANCE, ...(prev.appearance || {}), ...patch },
    }));
  }

  function handleRestoreAppearance() {
    setForm((prev) => ({
      ...prev,
      appearance: { ...DEFAULT_APPEARANCE },
    }));
    toast.success('Appearance restored to default');
  }

  // Handle Photo selection
  function handlePhotoFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setForm((prev) => ({ ...prev, photoUrl: e.target.result }));
      toast.success('Photo updated for preview');
    };
    reader.readAsDataURL(file);
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handlePhotoFile(e.dataTransfer.files[0]);
    }
  }

  // Copy booking link
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://civixcrm.ai';
  const effectiveSlug = form.bookingSlug || agent?.bookingSlug || 'agent';
  const fullBookingUrl = `${origin}/schedule/${effectiveSlug}`;
  const fullCardUrl = form.cardSlug ? `${origin}/c/${form.cardSlug}` : `${origin}/v/${agent?._id || 'me'}`;

  function copyBookingLink() {
    navigator.clipboard.writeText(fullBookingUrl);
    setCopiedLink(true);
    toast.success('Booking link copied to clipboard!');
    setTimeout(() => setCopiedLink(false), 2000);
  }

  async function handleSave(e) {
    if (e) e.preventDefault();
    if (!form.firstName.trim() && !form.lastName.trim()) {
      toast.error('Please enter at least a First or Last name');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cardSlug: form.cardSlug || undefined,
        attachDigitalBusinessCard: form.attachDigitalBusinessCard,
        vcard: {
          firstName: form.firstName,
          lastName: form.lastName,
          cellPhone: form.cellPhone,
          notes: form.notes,
          licenseInfo: form.licenseInfo,
          nationalId: form.nationalId,
          title: form.title,
          organization: form.organization,
          workUrl: form.workUrl,
          workEmail: form.workEmail,
          photoUrl: form.photoUrl,
          address: {
            label: form.addressLabel,
            street: form.street,
            city: form.city,
            state: form.state,
            zip: form.zip,
          },
          socials: {
            facebook: form.facebook,
            linkedin: form.linkedin,
          },
          appearance: form.appearance,
        },
        scheduler: {
          bookingSlug: form.bookingSlug,
          cap72Hours: form.cap72Hours,
        },
      };

      const res = await fetch('/api/user/vcard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to save Digital Business Card');
      }

      setInitialForm({ ...form });
      if (setSession && data.vcard) {
        setSession(token, {
          ...agent,
          name: `${form.firstName} ${form.lastName}`.trim() || agent.name,
          bookingSlug: form.bookingSlug,
          avatar: form.photoUrl || agent.avatar,
        });
      }

      toast.success('Digital Business Card saved!');
    } catch (err) {
      toast.error(err.message || 'Error saving settings');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-white/50 space-x-2">
        <RefreshCw className="w-5 h-5 animate-spin text-civix-purple" />
        <span>Loading Digital Business Card…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header section with Purple Tag Icon */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-white/10">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-civix-purple/20 border border-civix-purple/40 flex items-center justify-center text-civix-purple shadow-sm">
              <Tag className="w-5 h-5 text-civix-purple" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">Digital Business Card</h1>
          </div>
          <p className="text-sm text-white/60 mt-1">
            Create a digital business card with your contact information. Share it in text messages to your leads.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/booking-page')}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 hover:bg-white/15 text-white transition flex items-center gap-2 border border-white/10"
          >
            Next Step: Configure Booking Page →
          </button>
          {isDirty && (
            <span className="text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2.5 py-1 rounded-full flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Unsaved changes
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow-sm ${
              isDirty
                ? 'bg-civix-purple hover:bg-civix-purple/90 text-white shadow-civix-purple/30'
                : 'bg-white/10 text-white/40 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Sub-tabs: Information | Appearance */}
      <div className="flex border-b border-white/10 gap-6">
        <button
          type="button"
          onClick={() => setActiveSubTab('information')}
          className={`pb-3 text-sm font-semibold transition relative ${
            activeSubTab === 'information'
              ? 'text-white border-b-2 border-civix-purple'
              : 'text-white/50 hover:text-white/80'
          }`}
        >
          Information
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('appearance')}
          className={`pb-3 text-sm font-semibold transition relative ${
            activeSubTab === 'appearance'
              ? 'text-white border-b-2 border-civix-purple'
              : 'text-white/50 hover:text-white/80'
          }`}
        >
          Appearance
        </button>
      </div>

      {/* Main Grid: Form on Left | Preview on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Configuration Forms (7 Cols) */}
        <div className="lg:col-span-7 space-y-6">
          {activeSubTab === 'information' ? (
            <InformationSection
              form={form}
              setForm={setForm}
              handleTextChange={handleTextChange}
              fileInputRef={fileInputRef}
              handlePhotoFile={handlePhotoFile}
              copiedLink={copiedLink}
              copyBookingLink={copyBookingLink}
              fullBookingUrl={fullBookingUrl}
              agent={agent}
            />
          ) : (
            <AppearanceSection
              appearance={{ ...DEFAULT_APPEARANCE, ...(form.appearance || {}) }}
              onChange={handleAppearanceChange}
              onRestore={handleRestoreAppearance}
            />
          )}
        </div>

        <div className="lg:col-span-5 sticky top-6 space-y-4">
          <BusinessCardPreview form={form} agent={agent} fullBookingUrl={fullBookingUrl} fullCardUrl={fullCardUrl} />
          <CardLinkSection form={form} setForm={setForm} origin={origin} />
        </div>
      </div>
    </div>
  );
}
